import { IAppointmentDeprecatedService } from '@agent/core/contact/appointment-deprecated.service';
import { ModalParamAppointmentRescheduling } from '@agent/core/contact/appointment.service';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NG1_APPOINTMENT } from '@commons/core/core.provider.ajs';
import moment from 'moment';
import { BehaviorSubject, first, map } from 'rxjs';

import { AppointmentFacade, Logger, TimeSlot, TimeSlotType, ToasterFacade } from '@contact/upgrade';

export function commentModifiedValidator(originalComment: string): ValidatorFn {
  return (control: FormControl): { [key: string]: any } | null => {
    const modified = originalComment !== control.value;
    return modified ? null : { commentNotModified: { value: originalComment } };
  };
}

@Component({
  selector: 'cng-appointment-rescheduling-dialog',
  templateUrl: './appointment-rescheduling-dialog.component.html',
  styleUrls: ['./appointment-rescheduling-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentReschedulingDialogComponent implements OnInit, OnDestroy {
  // resolve params
  appointmentId: string;
  campaignId: string;
  originalTimeSlot: TimeSlot;

  // template related variables
  initDone: boolean = false;
  hasParamsError: boolean = false;
  private readonly _availableTimeSlots$ = new BehaviorSubject<TimeSlot[] | undefined>(undefined);
  readonly availableTimeSlots$ = this._availableTimeSlots$.asObservable();
  selectedTimeSlot: TimeSlot | undefined;
  comment: FormControl;

  constructor(
    private readonly logger: Logger,
    private readonly appointmentFacade: AppointmentFacade,
    private readonly toasterFacade: ToasterFacade,
    private readonly dialogRef: MatDialogRef<AppointmentReschedulingDialogComponent>,
    @Inject(NG1_APPOINTMENT) private readonly appointmentDeprecatedService: IAppointmentDeprecatedService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.logger = this.logger.forSource('AppointmentReschedulingDialogComponent');
  }

  ngOnInit(): void {
    // read the modal parameter, type of modalParamRecord
    let param: ModalParamAppointmentRescheduling = this.data;
    if (!!param && !!param.appointmentId && !!param.campaignId && !!param.originalTimeSlot) {
      this.appointmentId = param.appointmentId;
      this.campaignId = param.campaignId;
      this.originalTimeSlot = param.originalTimeSlot;
      this.comment = new FormControl(param.comment, [
        Validators.required,
        Validators.maxLength(255),
        commentModifiedValidator(param.comment),
      ]);
      if (
        !this.originalTimeSlot.beginDate ||
        !this.originalTimeSlot.endDate ||
        this.originalTimeSlot.endDate < this.originalTimeSlot.beginDate ||
        moment(this.originalTimeSlot.endDate).isBefore()
      ) {
        this.hasParamsError = true;
      } else {
        this.appointmentDeprecatedService.getAllScheduleCampaigns().then(campaigns => {
          this._availableTimeSlots$.next(
            campaigns
              .filter(c => c.id === this.campaignId)[0]
              .timeSlots.filter(timeSlot => timeSlot.type === TimeSlotType.OPENED && moment(timeSlot.endDate).isAfter())
          );
        });
      }
    } else {
      this.hasParamsError = true;
    }
    // when has something wrong in params, we should log in error
    if (this.hasParamsError) {
      this.logger.error('Missing params or params not utilisable for reschedule the appointment');
    }
    this.initDone = true;
  }

  ngOnDestroy(): void {
    this.appointmentId = this.campaignId = '';
    this.selectedTimeSlot = undefined;
  }

  onTimeSlotSelected(timeSlot: TimeSlot | undefined): void {
    this.selectedTimeSlot = timeSlot;
  }

  formIsValid(): boolean {
    return (
      !this.hasParamsError &&
      !!this.selectedTimeSlot && // selected time slot should not be same as the original time slot
      this.originalTimeSlot.id !== this.selectedTimeSlot.id &&
      this.originalTimeSlot.beginDate !== this.selectedTimeSlot.beginDate &&
      this.comment.valid
    );
  }

  submitForm(): void {
    if (!!this.selectedTimeSlot) {
      this.appointmentFacade.reschedule(this.appointmentId, this.selectedTimeSlot.id, this.comment.value);
      this.toasterFacade.toasts$
        .pipe(
          first(),
          map(toasts =>
            toasts.filter(
              toast => toast.data && (toast.data as any).apointmentId && (toast.data as any).apointmentId === this.appointmentId
            )
          )
        )
        .subscribe(toasts => toasts.forEach(toast => this.toasterFacade.close(toast.id)));
      this.logger.debug('The appointment ' + this.appointmentId + ' is to be rescheduled at ' + new Date(this.selectedTimeSlot.beginDate));
      this.close();
    }
  }

  canPrintCalendar(availableTimeSlots): boolean {
    return !!availableTimeSlots && availableTimeSlots.length > 0;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  close(): void {
    this.dialogRef.close(true);
  }
}
